import { Frame, RecordType } from "../../..";
import { RadarCameraExtrinsicFitter } from "../../../models/visualizer/FittingTool";
import {
    DistCoeffs,
    Intrinsics,
    polygonToChartDataset,
    radarDetectionToImageRegion,
} from "./calibrationGeometry";
import { DatasetConfig, TargetDatasetConfig } from "./DataChart";

const RADAR_TARGET_COLORS = [
    "#FF0000", "#00AAFF", "#00CC66", "#FFAA00",
    "#AA55FF", "#FF66AA", "#00CCCC", "#FF6600",
];

const FUSION_COLORS = [
    "#E6194B", "#3CB44B", "#4363D8", "#F58231",
    "#911EB4", "#46F0F0", "#F032E6", "#BCF60C",
    "#FABEBE", "#008080", "#E6BEFF", "#9A6324",
];

export type InferredObjectBounds = {
    inferredIndex: number;
    classId: number;
    targetType: number;
    confidence: number;
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
};

export type HoveredInferredObject = {
    inferredIndex: number;
    classId: number;
    targetType: number;
    confidence: number;
};

type InferredDetails = {
    inferredIndex: number;
    classId: number;
    targetType: number;
    confidence: number;
};

function findTlv(frame: Frame | undefined, type: RecordType) {
    return frame?.tlvs?.find(tlv => tlv.getDataType() === type);
}

export function buildRadarTargetDatasets(frame: Frame | undefined, frameIndex: number): TargetDatasetConfig[] {
    const trackStates = findTlv(frame, RecordType.RadarTarget)?.radarTargets;
    if (!trackStates?.length) return [];

    return trackStates.map((ts) => ({
        label: `Radar Target ${ts.trackId} (frame ${frameIndex})`,
        data: [{ x: ts.positionX, y: ts.positionY, z: ts.positionZ, targetId: ts.trackId }],
        baseColor: RADAR_TARGET_COLORS[ts.trackId % RADAR_TARGET_COLORS.length],
        selectedColor: "#00FF00",
        baseRadius: 6,
        selectedRadius: 8,
        pointStyle: "circle",
    }));
}

export function buildFusionTargetDatasets(frame: Frame | undefined): TargetDatasetConfig[] {
    if (!frame?.tlvs) return [];

    const radarTrackStates = findTlv(frame, RecordType.RadarTarget)?.radarTargets;
    const datasets: TargetDatasetConfig[] = [];

    if (radarTrackStates?.length) {
        datasets.push(...radarTrackStates.map((ts) => ({
            label: `Radar Target ${ts.trackId}`,
            data: [{ x: ts.positionX, y: ts.positionY, z: ts.positionZ, targetId: ts.trackId }],
            baseColor: FUSION_COLORS[ts.trackId % FUSION_COLORS.length],
            selectedColor: "#00FF00",
            baseRadius: 6,
            selectedRadius: 8,
            pointStyle: "triangle" as const,
        })));
    }

    const ioTrackStates = findTlv(frame, RecordType.CameraTarget)?.cameraTargetsV2;
    if (!ioTrackStates?.length) return datasets;

    datasets.push(...ioTrackStates.map((ts) => ({
        label: `Camera Target ${ts.trackId} (Radar: ${ts.associatedRadarId})`,
        data: [{ x: ts.positionX, y: ts.positionY, z: ts.positionZ, targetId: ts.trackId }],
        baseColor: ts.associatedRadarId === 0 ? "#ccc" : FUSION_COLORS[ts.associatedRadarId % FUSION_COLORS.length],
        selectedColor: "#00FF00",
        baseRadius: 6,
        selectedRadius: 8,
        pointStyle: "circle" as const,
    })));

    return datasets;
}

/**
 * Returns the radar track IDs in this frame, plus the subset that were fused
 * with a camera target in this frame. Callers can take the union of
 * `fusedRadarIds` across a time window to retroactively mark earlier
 * pre-association observations of the same track as fused.
 */
export function computeFrameFusionObservations(frame: Frame | undefined): {
    radarIds: number[];
    fusedRadarIds: number[];
} {
    if (!frame?.tlvs) return { radarIds: [], fusedRadarIds: [] };

    const radarTargets = findTlv(frame, RecordType.RadarTarget)?.radarTargets ?? [];
    const cameraTargets = findTlv(frame, RecordType.CameraTarget)?.cameraTargetsV2 ?? [];

    const associatedIds = new Set(
        cameraTargets.map(ct => ct.associatedRadarId).filter(id => id !== 0)
    );
    const radarIds = radarTargets.map(rt => rt.trackId);
    const fusedRadarIds = radarIds.filter(id => associatedIds.has(id));

    return { radarIds, fusedRadarIds };
}

export function buildRadarPointDatasets(frame: Frame | undefined): DatasetConfig[] {
    const pointClouds = findTlv(frame, RecordType.PointCloud)?.pointClouds;
    if (!pointClouds?.length) return [];

    const targetIndices = findTlv(frame, RecordType.RadarTargetIndex)?.targetIndices ?? [];
    const pointsByTarget = new Map<number, { x: number; y: number; z: number }[]>();

    pointClouds.forEach((pc, index) => {
        const targetIndex = targetIndices[index] ?? 255;
        if (!pointsByTarget.has(targetIndex)) {
            pointsByTarget.set(targetIndex, []);
        }
        pointsByTarget.get(targetIndex)!.push({
            x: pc.x ?? 0,
            y: pc.y ?? 0,
            z: pc.z ?? 0,
        });
    });

    const datasets: DatasetConfig[] = [];
    pointsByTarget.forEach((points, targetIndex) => {
        const isUnassociated = targetIndex === 255;
        const color = isUnassociated ? "#999999" : RADAR_TARGET_COLORS[targetIndex % RADAR_TARGET_COLORS.length];
        datasets.push({
            label: isUnassociated ? "Point Cloud (unassociated)" : `Point Cloud (target ${targetIndex})`,
            data: points,
            color,
            pointRadius: 3,
        });
    });

    return datasets;
}

/**
 * Build image-space box datasets for inferred objects. Shared by the Camera
 * and Calibration camera views (identical rendering).
 */
export function buildInferredObjectBoxDatasets(frame: Frame | undefined, imageHeight: number): DatasetConfig[] {
    const inferredObjects = findTlv(frame, RecordType.InferredObject)?.inferredObjects;
    if (!inferredObjects?.length) return [];

    return inferredObjects.map((obj, index) => {
        const x = obj.x;
        const y = obj.y;
        const width = obj.width;
        const height = obj.height;
        const yTop = imageHeight - y;
        const yBottom = imageHeight - (y + height);
        const details: InferredDetails = {
            inferredIndex: index + 1,
            classId: obj.classId,
            targetType: obj.targetType,
            confidence: obj.confidence,
        };

        return {
            label: `Inferred Object ${index + 1}`,
            data: [
                { x, y: yTop, z: 0, ...details },
                { x: x + width, y: yTop, z: 0, ...details },
                { x: x + width, y: yBottom, z: 0, ...details },
                { x, y: yBottom, z: 0, ...details },
                { x, y: yTop, z: 0, ...details },
            ],
            color: "#FFAA00",
            borderColor: "#FFAA00",
            borderWidth: 2,
            pointRadius: 0,
            showLine: true,
        };
    });
}

export function buildInferredObjectBounds(frame: Frame | undefined, imageHeight: number): InferredObjectBounds[] {
    const inferredObjects = findTlv(frame, RecordType.InferredObject)?.inferredObjects;
    if (!inferredObjects?.length) return [];

    return inferredObjects.map((obj, index) => {
        const yTop = imageHeight - obj.y;
        const yBottom = imageHeight - (obj.y + obj.height);
        return {
            inferredIndex: index + 1,
            classId: obj.classId,
            targetType: obj.targetType,
            confidence: obj.confidence,
            minX: obj.x,
            maxX: obj.x + obj.width,
            minY: yBottom,
            maxY: yTop,
        };
    });
}

export function findHoveredInferredObject(
    position: { x: number; y: number } | null,
    bounds: InferredObjectBounds[]
): HoveredInferredObject | null {
    if (!position) return null;
    const hit = bounds.find((obj) =>
        position.x >= obj.minX &&
        position.x <= obj.maxX &&
        position.y >= obj.minY &&
        position.y <= obj.maxY
    );
    if (!hit) return null;
    return {
        inferredIndex: hit.inferredIndex,
        classId: hit.classId,
        targetType: hit.targetType,
        confidence: hit.confidence,
    };
}

export type CalibrationAngles = {
    accPitch: number;
    accRoll: number;
    calPitch: number;
    calRoll: number;
    calYaw: number;
    deviceHeight: number;
    /** Whether the user has produced a calibration this session (or seeded one
     * from props). When true, the calibration views project radar points using
     * calYaw/calPitch/calRoll instead of the IMU-only fallback. */
    hasCalibration: boolean;
};

/**
 * Calibration radar view: radar target positions + inferred-object image points
 * projected back to the radar ground plane via the current fitter state.
 */
export function buildCalibrationRadarDatasets(
    frame: Frame | undefined,
    frameIndex: number,
    fitter: RadarCameraExtrinsicFitter | null,
    angles: CalibrationAngles
): DatasetConfig[] {
    if (!frame?.tlvs) return [];
    const datasets: DatasetConfig[] = [];

    const trackStates = findTlv(frame, RecordType.RadarTarget)?.radarTargets;
    trackStates?.forEach((ts) => {
        datasets.push({
            label: `Radar Target ${ts.trackId} (frame ${frameIndex})`,
            data: [{ x: ts.positionX, y: ts.positionY, z: ts.positionZ, targetId: ts.trackId }],
            color: "#FF0000",
            pointRadius: 6,
            pointStyle: "triangle",
        });
    });

    if (!fitter) return datasets;

    const inferredObjects = findTlv(frame, RecordType.InferredObject)?.inferredObjects?.filter((obj) => obj.classId !== 8);
    if (!inferredObjects?.length) return datasets;

    // When calibrated, use the fitted yaw/pitch/roll. Otherwise fall back to
    // the IMU-only pose (yaw unknown without calibration, so zero).
    const pitch = angles.hasCalibration ? angles.calPitch : angles.accPitch;
    const roll = angles.hasCalibration ? angles.calRoll : angles.accRoll;
    const yaw = angles.hasCalibration ? angles.calYaw : 0;

    inferredObjects.forEach((obj, index) => {
        const u = obj.x + obj.width / 2;
        const v = obj.y + obj.height * .75;
        const xypoint = fitter.projectImagePointToRadar(u, v, yaw, pitch, roll, angles.deviceHeight);

        datasets.push({
            label: `Camera Object ${index + 1}`,
            data: [{
                x: xypoint?.x ?? 0,
                y: xypoint?.y ?? 0,
                z: 0,
                targetId: index + 1,
            }],
            color: "#FFAA00",
            borderColor: "#FFAA00",
            borderWidth: 2,
            pointRadius: 3,
            showLine: true,
        });
    });

    return datasets;

}

/**
 * Calibration camera view: inferred-object boxes + radar target positions
 * projected into the image via the current fitter state.
 */
export function buildCalibrationCameraDatasets(
    frame: Frame | undefined,
    frameIndex: number,
    imageHeight: number,
    fitter: RadarCameraExtrinsicFitter | null,
    angles: CalibrationAngles
): DatasetConfig[] {
    if (!frame?.tlvs) return [];
    const datasets: DatasetConfig[] = [...buildInferredObjectBoxDatasets(frame, imageHeight)];

    if (!fitter) return datasets;

    const trackStates = findTlv(frame, RecordType.RadarTarget)?.radarTargets;
    if (!trackStates?.length) return datasets;

    trackStates.forEach((ts) => {
        const uvpoint = angles.hasCalibration
            ? fitter.projectRadarPointToImage(
                { x: ts.positionX, y: ts.positionY, z: ts.positionZ },
                angles.calYaw ?? 0, angles.calPitch ?? 0, angles.calRoll ?? 0, angles.deviceHeight)
            : fitter.radarPointToImageEstimate(ts.positionX, ts.positionY, ts.positionZ);

        datasets.push({
            label: `Radar Target ${ts.trackId} (frame ${frameIndex})`,
            data: [{
                x: uvpoint?.u ?? 0,
                y: imageHeight - (uvpoint?.v ?? 0),
                z: 0,
                targetId: ts.trackId,
            }],
            color: "#FF2200",
            pointRadius: 6,
        });
    });

    return datasets;
}

/**
 * Camera view: inferred-object boxes + radar point-cloud uncertainty regions
 * projected into the image using the accelerometer pitch/roll.
 */
export function buildCameraDatasets(
    frame: Frame | undefined,
    imageHeight: number,
    intrScaled: Intrinsics,
    dist: DistCoeffs,
    accPitch: number,
    accRoll: number,
    deviceHeight: number
): DatasetConfig[] {
    if (!frame?.tlvs) return [];
    const datasets: DatasetConfig[] = [...buildInferredObjectBoxDatasets(frame, imageHeight)];

    const pointClouds = findTlv(frame, RecordType.PointCloud)?.pointClouds;
    pointClouds?.forEach((pc, index) => {
        const poly = radarDetectionToImageRegion(pc.angle, pc.range, intrScaled, dist, accPitch, accRoll, deviceHeight);
        const data = polygonToChartDataset(poly, `Radar Region ${index}`, "#FF0000", imageHeight);
        if (data) datasets.push(data);
    });

    return datasets;
}
